import BaseApiService from "../../auditor/service/baseApiService.mjs";

class UserManagementService extends BaseApiService {
  constructor() {
    super();
    this.baseURL = '/user-management';
  }

  /**
   * Get all users (filtered by role permissions)
   */
  async getUsers(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.region) queryParams.append('region', params.region);
      if (params.role) queryParams.append('role', params.role);
      if (params.search) queryParams.append('search', params.search);
      if (params.includeDeleted) queryParams.append('includeDeleted', params.includeDeleted);

      const response = await this.api.get(`${this.baseURL}/users?${queryParams.toString()}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch users');
    }
  }

  /**
   * Get all regions (for Finance Director and Global Director)
   */
  async getRegions() {
    try {
      const response = await this.api.get(`${this.baseURL}/regions`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch regions');
    }
  }

  /**
   * Get statistics
   */
  async getStats() {
    try {
      const response = await this.api.get(`${this.baseURL}/stats`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch statistics');
    }
  }

  /**
   * Get roles that the current user can assign
   */
  async getAssignableRoles() {
    try {
      const response = await this.api.get(`${this.baseURL}/assignable-roles`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch assignable roles');
    }
  }

  /**
   * Change a user's role
   */
  async changeUserRole(userId, newRole, region = null) {
    try {
      const response = await this.api.patch(`${this.baseURL}/users/${userId}/role`, {
        newRole,
        region
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to change user role');
    }
  }

  /**
   * Mark someone staff or an independent contractor.
   *
   * Not a role change: a contractor has a claimant's permissions exactly. It
   * decides how far their own claims travel - a contractor's chain ends with
   * the auditor, where a staff member's carries on to the directors.
   */
  async changeUserEngagement(userId, engagementType) {
    try {
      const response = await this.api.patch(
        `${this.baseURL}/users/${userId}/engagement`,
        { engagementType }
      );
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to change engagement');
    }
  }

  /**
   * Suspend a user
   */
  async suspendUser(userId, reason = '') {
    try {
      const response = await this.api.post(`${this.baseURL}/users/${userId}/suspend`, {
        reason
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to suspend user');
    }
  }

  /**
   * Reactivate a suspended user
   */
  async reactivateUser(userId) {
    try {
      const response = await this.api.post(`${this.baseURL}/users/${userId}/reactivate`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reactivate user');
    }
  }

  /**
   * TEST ONLY: switch the signed-in user's own role. The server hard-gates this
   * to APP_ENVIRONMENT=test, so it is inert in production.
   */
  async setTestRole(role) {
    try {
      const response = await this.api.post(`${this.baseURL}/test/self-role`, { role });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to switch role');
    }
  }
}

const userManagementService = new UserManagementService();
export default userManagementService;
